import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  Heading,
  Text,
  VStack,
  Icon,
  useColorMode,
  Image,
  Badge,
  useDisclosure,
  HStack,
  Tag,
  Modal,
  ModalBody,
  ModalOverlay,
  ModalCloseButton,
  ModalContent,
  AspectRatio,
  useBreakpointValue,
  WrapItem,
  Wrap,
  Tooltip,
  useColorModeValue,
} from "@chakra-ui/react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  FiMessageSquare,
  FiGlobe,
  FiUploadCloud,
  FiSmartphone,
  FiClock,
  FiLayers,
  FiCpu,
  FiMic,
  FiChevronsDown,
  FiColumns,
  FiImage,
  FiVideo,
  FiServer,
  FiFolder,
  FiZap,
  FiUsers,
  FiTool,
} from "react-icons/fi";
import { RiRobotLine } from "react-icons/ri";
import { useTranslation } from "react-i18next";
import { PublicNavbar } from "./layout/PublicNavbar";
// import { DownloadSection } from "./DownloadSection";
import { FAQ } from "./FAQ";
import { Footer } from "./layout/Footer";
import { keyframes } from "@emotion/react";
import { aiAPI, systemAPI } from "../services";
import { AIModel } from "types";
import { ModelInfoModal } from "./model-info/ModelInfoModal";
import { FaBrain, FaCubes, FaUsers, FaUserSecret } from "react-icons/fa";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { SEO } from "./SEO";
import { MdCompareArrows, MdSlideshow, MdViewTimeline } from "react-icons/md";
import { Newsletter } from "./Newsletter";
import { blogAPI } from "services/blog-api";
import { BlogSection } from "./BlogSection";
import { AgentChat } from "./AgentChat";
import { TypeAnimation } from "react-type-animation";

const gradientAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const glowAnimation = keyframes`
  0% {
    opacity: 0.5;
    transform: translate(-50%, -50%) scale(0.8);
  }
  50% {
    opacity: 0.8;
    transform: translate(-50%, -50%) scale(1.2);
  }
  100% {
    opacity: 0.5;
    transform: translate(-50%, -50%) scale(0.8);
  }
`;
const pulseAnimation = keyframes`
  0% {
    opacity: 0.5;
    transform: translate(0%, 0%) scale(1);
  }
  50% {
    opacity: 0.8;
    transform: translate(0%, 0%) scale(1.1);
  }
  100% {
    opacity: 0.5;
    transform: translate(0%, 0%) scale(1);
  }
`;
const bounceAnimation = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-20px);
  }
  60% {
    transform: translateY(-10px);
  }
`;

const FeatureCard: React.FC<{
  icon: React.ElementType;
  title: string;
  description: string;
}> = ({ icon, title, description }) => {
  const { colorMode } = useColorMode();

  return (
    <Box
      p={6}
      borderRadius="xl"
      border="1px"
      borderColor={colorMode === "dark" ? "gray.700" : "gray.200"}
      _hover={{
        transform: "translateY(-5px)",
        boxShadow: "xl",
      }}
      transition="all 0.3s"
    >
      <VStack align="start" spacing={4}>
        <HStack>
          <Icon
            as={icon}
            w={6}
            h={6}
            color={colorMode === "dark" ? "brand.primary" : "brand.secondary"}
          />
          <Heading size="md" fontFamily="Nasalization">
            {title}
          </Heading>
        </HStack>
        <Text color={colorMode === "dark" ? "gray.400" : "gray.600"}>
          {description}
        </Text>
      </VStack>
    </Box>
  );
};

const galleryImages = [
  "https://runware.ai/assets/image-model03.WzAJ1X4v_2hyNLW.webp",
  "https://runware.ai/assets/image-model08.4Mre60Wh_189mlB.webp",
  "https://runware.ai/assets/image-model14.Le-thIZD_ZXFRM6.webp",
  "https://runware.ai/assets/image-model11.DXmm3hMN_Z1Rbek2.webp",
  "https://runware.ai/assets/image-model29.5zp8QgP9_daqPU.webp",
  "https://runware.ai/assets/image-model17.CU40_J1__1IjGyg.webp",
  "https://runware.ai/assets/image-model23.DLJR55st_Z2tI9Yj.webp",
  "https://runware.ai/assets/image-model28.yCVd1z0H_v7Eh3.webp",
  "https://runware.ai/assets/image-model01.Dstam6Lj_Zi45s6.webp",
  "https://runware.ai/assets/image-model25.C3yRrEPJ_2d6MhU.webp",
  "https://runware.ai/assets/image-model26.BrVeA8TZ_SF9KX.webp",
  "https://runware.ai/assets/image-model27.D13jSB-o_2uqhFl.webp",
  "https://runware.ai/assets/image-model19.B-PB3iMC_6Qilf.webp",
  "https://runware.ai/assets/image-model18.5DEPvvDZ_cHGNG.webp",
  "https://runware.ai/assets/image-model16.oFctiBQD_1mhnSF.webp",
  "https://runware.ai/assets/image-model30.BAs_8S2g_Z1poUA7.webp",
  "https://runware.ai/assets/image-model12.DBtAtBaX_Z13PYq5.webp",
  "https://runware.ai/assets/image-model10.DPUcMxSm_2kySpG.webp",
  "https://runware.ai/assets/image-model24.Cm3qbf8f_1UTzlh.webp",
  "https://runware.ai/assets/image-model22.CdO4xcmP_1yyiAW.webp",
  "https://runware.ai/assets/image-model09.BRBIk1JC_1IR9AF.webp",
  "https://runware.ai/assets/image-model13.JkwfW_XJ_Z27t6g3.webp",
  // "https://im.runware.ai/image/ws/2/ii/3252a162-79c5-4c19-b633-03ca4fdc59fc.jpg",
  // "https://im.runware.ai/image/ii/e9b95d69-7327-4a35-adf6-ca191ab0c125.JPG",
];

export const Home: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { colorMode } = useColorMode();
  const { t } = useTranslation();
  const {
    isOpen: isModelInfoOpen,
    onOpen: onModelInfoOpen,
    onClose: onModelInfoClose,
  } = useDisclosure();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [imageGallery, setImageGallery] = useState<string[]>(galleryImages);
  const [models, setModels] = useState<AIModel[]>([]);
  const [blogLatest, setBlogLatest] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currencyPairs, setCurrencyPairs] = useState<any>({ usd_brl: 7 });
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  // Pricing data from Mammouth.ai
  const prices = {
    openai: 20,
    anthropic: 20,
    google: 20,
    ourPriceUSD: 10,
    exchangeRate: 5, // This should come from .env
  };
  const totalCompetitors = prices.openai + prices.anthropic + prices.google;
  const savings = totalCompetitors - prices.ourPriceUSD;
  const ourPriceBRL = prices.ourPriceUSD * prices.exchangeRate;

  const features = [
    {
      icon: FaCubes,
      title: t("home.features.agents.title"),
      description: t("home.features.agents.description"),
    },
    {
      icon: FiTool,
      title: t("home.features.tools.title"),
      description: t("home.features.tools.description"),
    },
    {
      icon: FaUserSecret,
      title: t("home.features.characters.title"),
      description: t("home.features.characters.description"),
    },
    {
      icon: FiVideo,
      title: t("home.features.video.title"),
      description: t("home.features.video.description"),
    },
    {
      icon: FiImage,
      title: t("home.features.image.title"),
      description: t("home.features.image.description"),
    },
    {
      icon: FiColumns,
      title: t("home.features.sideBySide.title"),
      description: t("home.features.sideBySide.description"),
    },
    {
      icon: FiClock,
      title: t("home.features.history.title"),
      description: t("home.features.history.description"),
    },

    // {
    //   icon: FiMessageSquare,
    //   title: t("home.features.reprompting.title"),
    //   description: t("home.features.reprompting.description"),
    // },
    {
      icon: FaBrain,
      title: t("home.features.memory.title"),
      description: t("home.features.memory.description"),
    },
    {
      icon: FiGlobe,
      title: t("home.features.multilingual.title"),
      description: t("home.features.multilingual.description"),
    },
    {
      icon: FiFolder,
      title: t("home.features.folders.title"),
      description: t("home.features.folders.description"),
    },
    // {
    //   icon: FaUsers,
    //   title: t("home.features.users.title"),
    //   description: t("home.features.users.description"),
    // },
    {
      icon: MdSlideshow,
      title: t("home.features.slides.title"),
      description: t("home.features.slides.description"),
    },
    {
      icon: FiServer,
      title: t("home.features.api.title"),
      description: t("home.features.api.description"),
    },

    // {
    //   icon: FiSmartphone,
    //   title: t("home.features.devices.title"),
    //   description: t("home.features.devices.description"),
    // },
  ];

  const futureFeatures = [
    {
      icon: FiUploadCloud,
      title: t("home.features.upload.title"),
      description: t("home.features.upload.description"),
    },
    {
      icon: RiRobotLine,
      title: t("home.futureFeatures.agents.title"),
      description: t("home.futureFeatures.agents.description"),
    },
    // {
    //   icon: FiLayers,
    //   title: t("home.futureFeatures.assistant.title"),
    //   description: t("home.futureFeatures.assistant.description"),
    // },
    {
      icon: FiCpu,
      title: t("home.futureFeatures.training.title"),
      description: t("home.futureFeatures.training.description"),
    },
    {
      icon: FiMic,
      title: t("home.futureFeatures.speech.title"),
      description: t("home.futureFeatures.speech.description"),
    },
  ];

  const loadCurrencyPairs = async () => {
    const currencyPairs = await systemAPI.getCurrencyPairs();
    // console.log("loaded price pairs", currencyPairs);
    setCurrencyPairs(currencyPairs);
  };

  const loadBlogLatest = async () => {
    const blogLatest = await blogAPI.getWordPressPosts();
    setBlogLatest(blogLatest);
  };

  // Add function to load models
  const loadModels = async () => {
    try {
      setIsLoading(true);
      const modelData = await aiAPI.getModels();
      setModels(modelData);
    } catch (error) {
      console.error("Error loading models:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Load models when modal opens
  useEffect(() => {
    if (isModelInfoOpen) {
      loadModels();
      loadCurrencyPairs();
    }
  }, [isModelInfoOpen]);

  // Handle referral code from URL
  useEffect(() => {
    const referralCode = searchParams.get("aff");
    if (referralCode) {
      // Store referral code in session storage
      sessionStorage.setItem("referralCode", referralCode);
    }
  }, [searchParams]);

  // randomize image gallery
  useEffect(() => {
    setImageGallery(imageGallery.sort(() => Math.random() - 0.5));
    loadBlogLatest();
    loadModels();
  }, []);

  const isMobile = useBreakpointValue({ base: true, md: false });
  const items = t("home.typeAnimationFeatures", {
    returnObjects: true,
  }) as { title: string }[];

  // randomize items
  const randomItems = items.sort(() => Math.random() - 0.5);

  // Create sequence array for TypeAnimation
  const sequence = randomItems.flatMap((item): (string | number)[] => [
    item.title,
    5000, // pause for 3 seconds
  ]);
  return (
    <>
      <SEO />
      <Box>
        <PublicNavbar />

        {/* Hero Section */}
        <Box
          position="relative"
          overflow="hidden"
          py={40}
          borderBottom="1px"
          borderColor={colorMode === "dark" ? "gray.700" : "gray.200"}
        >
          {/* Animated background */}
          <Box
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            bgGradient={
              colorMode === "dark"
                ? "linear(to-r, gray.900, brand.secondary, gray.900)"
                : "linear(to-r, white, brand.primary, white)"
            }
            backgroundSize="400% 400%"
            animation={`${gradientAnimation} 15s ease infinite`}
            opacity="0.1"
          />

          {/* Glowing orbs */}
          <Box
            position="absolute"
            top="90%"
            left="50%"
            width="800px"
            height="800px"
            borderRadius="full"
            bg={colorMode === "dark" ? "brand.secondary" : "white"}
            filter="blur(80px)"
            opacity="0.15"
            animation={`${glowAnimation} 7s ease-in-out infinite`}
            transform="translate(-50%, -50%)"
          />
          <Box
            position="absolute"
            top="0%"
            left="75%"
            width="300px"
            height="300px"
            borderRadius="full"
            bg={colorMode === "dark" ? "brand.secondary" : "white"}
            filter="blur(80px)"
            opacity="0.15"
            animation={`${glowAnimation} 7s ease-in-out infinite 1s`}
            transform="translate(-50%, -50%)"
          />

          {/* Content */}
          <Container maxW="container.lg" mt={10} position="relative">
            <VStack spacing={12} textAlign="center">
              <VStack spacing={8}>
                <Heading
                  size="2xl"
                  fontFamily="Nasalization"
                  bgGradient="linear(to-r, brand.primary, brand.secondary)"
                  bgClip="text"
                >
                  {t("home.hero.title")}
                </Heading>
                <Box maxW="2xl" minH="60px">
                  <TypeAnimation
                    sequence={sequence}
                    wrapper="span"
                    speed={89}
                    style={{
                      fontSize: "1.25rem",
                      display: "inline-block",
                      fontFamily: "Nasalization",
                      color: useColorModeValue("#00392b", "#a0aec0"),
                    }}
                    repeat={Infinity}
                  />
                </Box>
                {/* <Text
                  fontSize="xl"
                  color={colorMode === "dark" ? "gray.400" : "gray.600"}
                  maxW="2xl"
                >
                  {t("home.hero.subtitle")}
                </Text> */}
                {/* <Button
                  size="lg"
                  bg="brand.primary"
                  color="black"
                  _hover={{
                    bg: "brand.secondary",
                    color: "white",
                  }}
                  onClick={() => navigate("/pricing")}
                  fontFamily="Nasalization"
                >
                  {t("home.hero.cta")}
                </Button> */}
                <Heading
                  size="1xl"
                  fontFamily="Nasalization"
                  bgGradient="linear(to-r, brand.primary, green.500)"
                  bgClip="text"
                  borderRadius="lg"
                  p={2}
                  border="1px"
                  borderColor="green.500"
                  cursor="pointer"
                  sx={{
                    "&:hover": {
                      bg: "green.500",
                      color: "white",
                    },
                  }}
                  onClick={() => navigate("/register")}
                >
                  {t("home.hero.titleBottom")}
                </Heading>
                <Text fontSize="sm" color="gray.500">
                  {t("home.hero.titleBottomDescription")}
                </Text>
                <Wrap spacing={2} justify="center" maxW="2xl">
                  {models &&
                    models.length > 0 &&
                    models
                      .filter(
                        (model) =>
                          model.inputCostPer1M === 0 &&
                          model.outputCostPer1M === 0 &&
                          model.type === "llm" &&
                          model.capabilities &&
                          model.capabilities.includes("chat")
                      )
                      .map((model) => (
                        <WrapItem key={model.id}>
                          <Tooltip label={model.provider}>
                            <Tag>{model.name}</Tag>
                          </Tooltip>
                        </WrapItem>
                      ))}
                </Wrap>
              </VStack>

              {/* Pricing Comparison */}
              {/* <Box
                p={8}
                borderRadius="xl"
                bg={colorMode === 'dark' ? 'gray.800' : 'white'}
                borderWidth="1px"
                borderColor={colorMode === 'dark' ? 'gray.700' : 'gray.200'}
                w="full"
                maxW="3xl"
              >
                <VStack spacing={6}>
                  <Heading size="lg" color={colorMode === 'dark' ? 'brand.primary' : 'brand.secondary'}>
                    {t('home.hero.pricing.compare')}
                  </Heading>
                  <Grid templateColumns="1fr auto" gap={4} w="full">
                    <Text fontWeight="bold" color={colorMode === 'dark' ? 'gray.400' : 'gray.600'}>
                      {t('home.hero.pricing.individual')}:
                    </Text>
                    <Text fontWeight="bold">
                      {t('home.hero.pricing.currency.usd')}{totalCompetitors}{t('home.hero.pricing.perMonth')}
                    </Text>
                    <Text color={colorMode === 'dark' ? 'gray.400' : 'gray.600'}>
                      {t('home.hero.pricing.openai')}
                    </Text>
                    <Text>{t('home.hero.pricing.currency.usd')}{prices.openai}</Text>
                    <Text color={colorMode === 'dark' ? 'gray.400' : 'gray.600'}>
                      {t('home.hero.pricing.anthropic')}
                    </Text>
                    <Text>{t('home.hero.pricing.currency.usd')}{prices.anthropic}</Text>
                    <Text color={colorMode === 'dark' ? 'gray.400' : 'gray.600'}>
                      {t('home.hero.pricing.google')}
                    </Text>
                    <Text>{t('home.hero.pricing.currency.usd')}{prices.google}</Text>
                    <Box h="1px" bg={colorMode === 'dark' ? 'gray.600' : 'gray.300'} gridColumn="1/-1" my={2} />
                    <Text fontWeight="bold" color="brand.primary">
                      {t('home.hero.pricing.combined')}:
                    </Text>
                    <HStack spacing={2}>
                      <Text fontWeight="bold" color="brand.primary">
                        {t('home.hero.pricing.currency.usd')}{prices.ourPriceUSD}
                      </Text>
                      <Text color={colorMode === 'dark' ? 'gray.400' : 'gray.600'}>
                        ({t('home.hero.pricing.currency.brl')}{ourPriceBRL.toFixed(2)})
                      </Text>
                    </HStack>
                    <Text fontSize="sm" color="green.400" gridColumn="1/-1">
                      {t('home.hero.pricing.save')} {t('home.hero.pricing.currency.usd')}{savings}{t('home.hero.pricing.perMonth')}
                    </Text>
                  </Grid>
                </VStack>
                <Button
                  size="lg"
                  mt={4}
                  bg="brand.primary"
                  color="black"
                  _hover={{
                    bg: 'brand.secondary',
                    color: 'white',
                  }}
                  onClick={() => navigate('/pricing')}
                  fontFamily="Nasalization"
                >
                  {t('home.hero.pricing.cta')}
                </Button>
              </Box> */}
            </VStack>
          </Container>
        </Box>

        {/* Providers Section */}
        <Box py={20} bg={colorMode === "dark" ? "gray.800" : "gray.100"}>
          <Container maxW="container.lg">
            <VStack spacing={16}>
              <VStack spacing={4} textAlign="center">
                <Heading
                  size="xl"
                  fontFamily="Nasalization"
                  color={
                    colorMode === "dark" ? "brand.primary" : "brand.secondary"
                  }
                >
                  {t("home.providers.title")}
                </Heading>
                <Text
                  fontSize="lg"
                  color={colorMode === "dark" ? "gray.400" : "gray.600"}
                  maxW="2xl"
                >
                  {t("home.providers.subtitle")}
                </Text>
              </VStack>
              <Grid
                templateColumns={{
                  base: "repeat(2, 1fr)",
                  md: "repeat(4, 1fr)",
                  lg: "repeat(4, 1fr)",
                }}
                gap={4}
                w="full"
              >
                {[
                  {
                    name: "openai",
                    soon: false,
                    worth: "$20",
                    tags: ["GPT-4o", "O1", "O3-mini"],
                    invert: true,
                  },
                  {
                    name: "anthropic",
                    soon: false,
                    worth: "$20",
                    tags: ["Haiku", "Sonnet", "Opus"],
                    invert: true,
                  },
                  {
                    name: "mistral",
                    soon: false,
                    worth: "",
                    tags: ["Small", "Large", "Pixtral"],
                    invert: false,
                  },
                  // {
                  //   name: "groq",
                  //   soon: false,
                  //   worth: "",
                  //   tags: ["Llama 70b", "Llama 8b"],
                  // },
                  {
                    name: "google",
                    soon: false,
                    worth: "",
                    tags: [
                      "Gemini Pro",
                      "Gemini Flash",
                      "Gemini Flash Thinking",
                    ],
                    invert: false,
                  },

                  {
                    name: "meta",
                    soon: false,
                    worth: "",
                    tags: ["Llama 70b", "Llama 8b"],
                    invert: false,
                  },
                  {
                    name: "deepseek",
                    soon: false,
                    worth: "",
                    tags: ["Chat V3", "Reasoner R1"],
                    invert: false,
                  },
                  {
                    name: "maritaca",
                    soon: false,
                    worth: "",
                    tags: ["Sabiá 3", "Sabiázinho 3"],
                    invert: false,
                  },
                  {
                    name: "alibaba",
                    soon: false,
                    worth: "",
                    tags: ["Qwen Max", "Qwen Turbo", "Qwen Plus"],
                    invert: false,
                  },
                  // { name: "runware", soon: false, worth: "" },
                  {
                    name: "flux",
                    soon: false,
                    worth: "$10",
                    tags: ["Schnell", "Dev"],
                    invert: true,
                  },
                  {
                    name: "civitai",
                    soon: false,
                    worth: "",
                    tags: ["> 300k models"],
                    invert: true,
                  },
                  {
                    name: "stabilityai",
                    soon: false,
                    worth: "",
                    tags: ["SD 3", "SDXL", "SD 1.5"],
                    invert: false,
                  },
                  {
                    name: "kling",
                    soon: false,
                    worth: "$25.99",
                    invert: false,
                    tags: ["v1.5", "v1.6", "Pro & Std"],
                  },
                  { name: "decor8", soon: false, worth: "$20", invert: true },
                  {
                    name: "cohere",
                    soon: false,
                    worth: "",
                    tags: ["Rerank v3.5", "Command R", "Command R+"],
                    invert: false,
                  },
                  { name: "mxbai", soon: false, worth: "", invert: false },
                  {
                    name: "midjourney",
                    soon: false,
                    worth: "$10",
                    invert: true,
                    tags: ["image", "upscale", "variation"],
                  },
                  {
                    name: "suno",
                    soon: false,
                    worth: "",
                    invert: true,
                    tags: ["v4"],
                  },
                  {
                    name: "perplexity",
                    soon: true,
                    worth: "",
                    tags: ["Sonar Reasoning", "Sonar Reasoning Pro"],
                    invert: true,
                  },
                  {
                    name: "runway",
                    soon: true,
                    worth: "",
                    invert: true,
                    tags: ["Gen 3 Alpha Turbo"],
                  },
                  {
                    name: "playht",
                    soon: true,
                    worth: "$39",
                    invert: true,
                    tags: ["tts", "voice-cloning"],
                  },

                  // { name: "ollama", soon: true, worth: "local", invert: true },
                ].map((provider) => (
                  <VStack
                    key={provider.name}
                    p={4}
                    borderRadius="lg"
                    bg={colorMode === "dark" ? "gray.900" : "white"}
                    borderWidth="1px"
                    borderColor={colorMode === "dark" ? "gray.700" : "gray.200"}
                    spacing={2}
                    _hover={{
                      transform: "translateY(-5px)",
                      boxShadow: "xl",
                    }}
                    transition="all 0.3s"
                    opacity={provider.soon ? 0.6 : 1}
                    position="relative"
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                  >
                    {provider.worth && (
                      <Badge
                        position="absolute"
                        top={2}
                        left={2}
                        colorScheme="purple"
                        fontSize="xs"
                      >
                        {provider.worth}
                      </Badge>
                    )}
                    {provider.soon && (
                      <Badge
                        position="absolute"
                        top={2}
                        right={2}
                        colorScheme="purple"
                        fontSize="xs"
                      >
                        {t("home.soon")}
                      </Badge>
                    )}
                    <Image
                      src={`./assets/img/${provider.name}.png`}
                      alt={t(`home.providers.${provider.name}`)}
                      height="40px"
                      filter={
                        colorMode === "dark"
                          ? provider.invert
                            ? "brightness(0) invert(1)"
                            : "brightness(1) invert(0)"
                          : undefined
                      }
                    />
                    <Text fontWeight="bold">
                      {t(`home.providers.${provider.name}`)}
                    </Text>
                    <Wrap spacing={1} justify="center">
                      {provider.tags &&
                        provider.tags.map((tag) => (
                          <WrapItem key={tag}>
                            <Tag size="sm">{tag}</Tag>
                          </WrapItem>
                        ))}
                    </Wrap>
                  </VStack>
                ))}
              </Grid>

              <VStack spacing={1} alignItems="center" width="100%">
                <Icon
                  as={FiChevronsDown}
                  w={20}
                  h={20}
                  color={
                    colorMode === "dark" ? "brand.primary" : "brand.secondary"
                  }
                  animation={`${bounceAnimation} 2s ease-in-out infinite`}
                />
                <Image
                  src={
                    colorMode === "dark"
                      ? "./assets/img/logo-circle-dark.svg"
                      : "./assets/img/logo-circle-light.svg"
                  }
                  alt="Araria"
                  height="150px"
                  animation={`${pulseAnimation} 3s ease-in-out infinite`}
                />
              </VStack>

              <Button
                size="lg"
                bg={colorMode === "dark" ? "brand.primary" : "brand.secondary"}
                color={colorMode === "dark" ? "brand.secondary" : "white"}
                _hover={{
                  bg: "brand.secondary",
                  color: "white",
                }}
                onClick={onModelInfoOpen}
                fontFamily="Nasalization"
              >
                {t("home.providers.viewModels")}
              </Button>

              {/* Model Info Modal */}
              <ModelInfoModal
                isOpen={isModelInfoOpen}
                onClose={onModelInfoClose}
                models={models}
                isLoading={isLoading}
                isModelInfoOpen={isModelInfoOpen}
                onModelInfoClose={onModelInfoClose}
                currencyPairs={currencyPairs}
              />
            </VStack>
          </Container>
        </Box>

        {/* Third Party Section */}
        {/* <Box py={20} bg={colorMode === "dark" ? "gray.800" : "gray.100"}>
          <Container maxW="container.lg">
            <VStack spacing={16}>
              <VStack spacing={4} textAlign="center">
                <Heading
                  size="xl"
                  fontFamily="Nasalization"
                  color={
                    colorMode === "dark" ? "brand.primary" : "brand.secondary"
                  }
                >
                  {t("home.thirdParty.title")}
                </Heading>
                <Text
                  fontSize="lg"
                  color={colorMode === "dark" ? "gray.400" : "gray.600"}
                  maxW="2xl"
                >
                  {t("home.thirdParty.subtitle")}
                </Text>
              </VStack>
              <Grid
                templateColumns={{
                  base: "1fr",
                  md: "repeat(2, 1fr)",
                  lg: "repeat(3, 1fr)",
                }}
                gap={4}
                w="full"
                maxW="container.md"
                mx="auto"
                justifyContent="center"
                alignItems="center"
              >
                {[
                  {
                    name: "whatsapp",
                    invert: false,
                  },
                  {
                    name: "twilio",
                    invert: false,
                  },
                  {
                    name: "contentful",
                    invert: false,
                  },
                ].map((provider) => (
                  <VStack
                    key={provider.name}
                    p={4}
                    borderRadius="lg"
                    bg={colorMode === "dark" ? "gray.900" : "white"}
                    borderWidth="1px"
                    borderColor={colorMode === "dark" ? "gray.700" : "gray.200"}
                    spacing={2}
                    _hover={{
                      transform: "translateY(-5px)",
                      boxShadow: "xl",
                    }}
                    transition="all 0.3s"
                    position="relative"
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Image
                      src={`./assets/img/${provider.name}.png`}
                      alt={t(`home.providers.${provider.name}`)}
                      height="40px"
                      filter={
                        colorMode === "dark"
                          ? provider.invert
                            ? "brightness(0) invert(1)"
                            : "brightness(1) invert(0)"
                          : undefined
                      }
                    />
                    <Text fontWeight="bold">
                      {t(`home.providers.${provider.name}`)}
                    </Text>
                  </VStack>
                ))}
              </Grid>
            </VStack>
          </Container>
        </Box> */}

        {/* Features Section */}
        <Box py={20} bg={colorMode === "dark" ? "gray.900" : "white"}>
          <Container maxW="container.lg">
            <VStack spacing={16}>
              <VStack spacing={4} textAlign="center">
                <Heading
                  size="xl"
                  fontFamily="Nasalization"
                  color={
                    colorMode === "dark" ? "brand.primary" : "brand.secondary"
                  }
                >
                  {t("home.features.title")}
                </Heading>
                <Text
                  fontSize="lg"
                  color={colorMode === "dark" ? "gray.400" : "gray.600"}
                  maxW="2xl"
                >
                  {t("home.features.subtitle")}
                </Text>
              </VStack>

              <Grid
                templateColumns={{
                  base: "1fr",
                  md: "repeat(2, 1fr)",
                  lg: "repeat(3, 1fr)",
                }}
                gap={4}
                w="full"
                maxW="container.md"
                mx="auto"
                justifyContent="center"
                alignItems="center"
              >
                {features.map((feature, index) => (
                  <FeatureCard key={index} {...feature} />
                ))}
              </Grid>
            </VStack>
          </Container>
        </Box>

        {/* Images Section */}
        <Box py={20} bg={colorMode === "dark" ? "gray.800" : "gray.50"}>
          <Container maxW="full">
            <VStack spacing={16}>
              <VStack spacing={4} textAlign="center">
                <Heading
                  size="xl"
                  fontFamily="Nasalization"
                  color={
                    colorMode === "dark" ? "brand.primary" : "brand.secondary"
                  }
                >
                  {t("home.gallery.title")}
                </Heading>
                <Text
                  fontSize="lg"
                  color={colorMode === "dark" ? "gray.400" : "gray.600"}
                  maxW="2xl"
                >
                  {t("home.gallery.subtitle")}
                </Text>
              </VStack>

              <Box w="full" position="relative">
                <Carousel
                  showThumbs={false}
                  showStatus={false}
                  // infiniteLoop
                  autoPlay
                  interval={3000}
                  swipeable
                  emulateTouch
                  centerMode
                  centerSlidePercentage={isMobile ? 40 : 15}
                  selectedItem={0}
                  showArrows={false}
                  showIndicators={isMobile ? false : true}
                >
                  {imageGallery.map((imageId, index) => (
                    <Box key={index} cursor="pointer">
                      <Image
                        src={imageId}
                        alt={`Gallery Image ${imageId}`}
                        objectFit="cover"
                        loading="lazy"
                        maxW={isMobile ? "100%" : "300px"}
                        // h={isMobile ? "250px" : "auto"}
                        minH={isMobile ? "250px" : "450px"}
                        borderRadius="xl"
                        fallback={
                          <Box
                            width={isMobile ? "100%" : "300px"}
                            height={isMobile ? "200px" : "300px"}
                            bg="gray.200"
                            borderRadius="xl"
                          />
                        }
                      />
                    </Box>
                  ))}
                </Carousel>
              </Box>
            </VStack>
          </Container>

          <Modal isOpen={isOpen} onClose={onClose} size="6xl">
            <ModalOverlay />
            <ModalContent bg="transparent" boxShadow="none">
              <ModalCloseButton color="white" />
              <ModalBody p={0}>
                <Image
                  src={selectedImage || ""}
                  alt="Selected gallery image"
                  w="full"
                  borderRadius="xl"
                />
              </ModalBody>
            </ModalContent>
          </Modal>
        </Box>

        {/* Video Section */}
        <Box
          py={20}
          bg={colorMode === "dark" ? "gray.800" : "gray.50"}
          position="relative"
          overflow="hidden"
        >
          {/* Background Video */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            zIndex={0}
          >
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              bg={colorMode === "dark" ? "gray.900" : "gray.50"}
              opacity={0.8}
              zIndex={1}
            />
            <video
              autoPlay
              muted
              loop
              playsInline
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
              onLoadedData={(e) => {
                const video = e.target as HTMLVideoElement;
                const observer = new IntersectionObserver(
                  ([entry]) => {
                    if (entry.isIntersecting) {
                      video.play();
                    } else {
                      video.pause();
                    }
                  },
                  { threshold: 0.1 }
                );
                observer.observe(video);
              }}
            >
              <source
                src="https://d3phaj0sisr2ct.cloudfront.net/site/videos/footer-videos/Hyperlapse.mp4"
                type="video/mp4"
              />
            </video>
          </Box>

          {/* Content */}
          <Container maxW="container.lg" position="relative" zIndex={2}>
            <VStack spacing={16}>
              <VStack spacing={4} textAlign="center">
                <Heading
                  size="xl"
                  fontFamily="Nasalization"
                  color={
                    colorMode === "dark" ? "brand.primary" : "brand.secondary"
                  }
                >
                  {t("home.video.title") || "See it in Action"}
                </Heading>
                <Text
                  fontSize="lg"
                  color={colorMode === "dark" ? "gray.400" : "gray.600"}
                  maxW="2xl"
                >
                  {t("home.video.subtitle") ||
                    "Watch how our platform transforms your AI workflow"}
                </Text>
                {/* <Button
                  as={Link}
                  to="/video"
                  size="lg"
                  bg="brand.primary"
                  color="black"
                >
                  {t("home.video.showcase")}
                </Button> */}
              </VStack>

              {/* <Box
                position="relative"
                width="100%"
                maxW="1000px"
                borderRadius="xl"
                overflow="hidden"
                boxShadow="2xl"
              >
                <AspectRatio ratio={16 / 9}>
                  <iframe
                    src="https://d3phaj0sisr2ct.cloudfront.net/site/videos/footer-videos/footer-01.mp4"
                    title="Product Demo"
                    allowFullScreen
                    loading="lazy"
                    style={{
                      borderRadius: "xl",
                      border: "none",
                    }}
                  />
                </AspectRatio>
              </Box> */}

              <Grid
                templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
                gap={8}
                width="100%"
              >
                {[
                  {
                    title: t("home.video.feature1.title") || "Easy Integration",
                    description:
                      t("home.video.feature1.description") ||
                      "Connect with multiple AI providers in minutes",
                    icon: FiZap,
                  },
                  {
                    title:
                      t("home.video.feature2.title") || "Powerful Interface",
                    description:
                      t("home.video.feature2.description") ||
                      "Intuitive design for maximum productivity",
                    icon: MdCompareArrows,
                  },
                  {
                    title:
                      t("home.video.feature3.title") ||
                      "Real-time Collaboration",
                    description:
                      t("home.video.feature3.description") ||
                      "Work together seamlessly across teams",
                    icon: FiClock,
                  },
                ].map((feature, index) => (
                  <VStack
                    key={index}
                    p={6}
                    bg={
                      colorMode === "dark"
                        ? "rgba(26, 32, 44, 0.7)"
                        : "rgba(255, 255, 255, 0.7)"
                    }
                    borderRadius="xl"
                    borderWidth="1px"
                    borderColor={colorMode === "dark" ? "gray.700" : "gray.200"}
                    spacing={4}
                    _hover={{
                      transform: "translateY(-5px)",
                      boxShadow: "xl",
                    }}
                    transition="all 0.3s"
                  >
                    <Icon
                      as={feature.icon}
                      w={8}
                      h={8}
                      color={
                        colorMode === "dark"
                          ? "brand.primary"
                          : "brand.secondary"
                      }
                    />
                    <Heading size="md" fontFamily="Nasalization">
                      {feature.title}
                    </Heading>
                    <Text
                      color={colorMode === "dark" ? "gray.400" : "gray.600"}
                      textAlign="center"
                    >
                      {feature.description}
                    </Text>
                  </VStack>
                ))}
              </Grid>
            </VStack>
          </Container>
        </Box>

        {/* Future Features Section */}
        <Box
          py={20}
          sx={{
            display: "none",
          }}
        >
          <Container maxW="container.lg">
            <VStack spacing={16}>
              <VStack spacing={4} textAlign="center">
                <Heading
                  size="xl"
                  fontFamily="Nasalization"
                  color={
                    colorMode === "dark" ? "brand.primary" : "brand.secondary"
                  }
                >
                  {t("home.futureFeatures.title")}
                </Heading>
                <Text
                  fontSize="lg"
                  color={colorMode === "dark" ? "gray.400" : "gray.600"}
                  maxW="2xl"
                >
                  {t("home.futureFeatures.subtitle")}
                </Text>
              </VStack>

              <Grid
                templateColumns={{
                  base: "1fr",
                  md: "repeat(2, 1fr)",
                  lg: "repeat(3, 1fr)",
                }}
                gap={8}
              >
                {futureFeatures.map((feature, index) => (
                  <FeatureCard key={index} {...feature} />
                ))}
              </Grid>
            </VStack>
          </Container>
        </Box>

        {/* Download Section */}
        {/* <DownloadSection /> */}

        {/* FAQ Section */}
        {/* <FAQ /> */}

        {/* Agent Chat Section */}

        <AgentChat />

        {/* Blog Section */}
        <BlogSection blogLatest={blogLatest} />

        {/* Newsletter */}
        {/* <Box>
          <Newsletter />
        </Box> */}
        <Footer />
      </Box>
    </>
  );
};
